// frontend/src/components/HistoryModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, ButtonGroup } from 'react-bootstrap';
import api from '../api';
import JackpotChart from './JackpotChart';
import './HistoryModal.css';

export default function HistoryModal({ show, onHide, deviceId }) {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' 或 'chart'
  const [chartType, setChartType] = useState('line'); // 'line' 或 'bar'
  
  // 获取历史数据
  useEffect(() => {
    if (show && deviceId) {
      fetchJackpotHistory();
    }
  }, [show, deviceId]);

  // 监听屏幕尺寸变化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 从后端API获取设备的jackpot履历数据
  const fetchJackpotHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      // 获取设备信息，包含histogramBars字段
      const response = await api.get(`/api/devices/${deviceId}`);
      const device = response.data;
      
      // 保存设备信息
      setDeviceInfo(device);
      
      if (device && device.histogramBars) {
        // 将histogramBars转换为显示格式
        const transformedData = transformJackpotHistoryToGrid(device.histogramBars);
        setHistoryData(transformedData);
      } else {
        // 如果没有数据，使用空数组
        setHistoryData([]);
      }
    } catch (err) {
      console.error('获取设备jackpot履历失败:', err);
      setError(err.response?.data?.message || '获取履历数据失败');
      // 如果API失败，使用模拟数据作为后备
      const mockData = generateMockHistoryData();
      setHistoryData(mockData);
    } finally {
      setLoading(false);
    }
  };

  // 将设备的histogramBars数据转换为弹珠机履历显示格式
  const transformJackpotHistoryToGrid = (histogramBars) => {
    // 按时间排序，最新的在前
    const sortedBars = [...histogramBars].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
    // 显示更多记录，支持滑动查看
    const maxRecords = isMobile ? 50 : 100; // 增加显示数量
    const limitedBars = sortedBars.slice(0, maxRecords);
    
    return limitedBars.map((bar, roundIndex) => {
      // 使用实际的转数和连奖数据
      const totalTurns = bar.turnCount || 0;
      const consecutiveWins = bar.consecutivePrize || 0;
      
      // 每100转一个格子
      const totalGrids = Math.ceil(totalTurns / 100);
      
      const grids = [];
      // 限制最多10格
      const maxGrids = Math.min(10, totalGrids);
      
      for (let gridIndex = 0; gridIndex < maxGrids; gridIndex++) {
        const turns = Math.min(100, Math.max(0, totalTurns - gridIndex * 100));
        const isActive = gridIndex < totalGrids && turns > 0;
        
        grids.push({
          id: `${roundIndex}-${gridIndex}`,
          turns: isActive ? turns : 0,
          isActive: isActive,
          roundIndex: roundIndex,
          gridIndex: gridIndex
        });
      }
      
      return {
        id: roundIndex,
        totalTurns: totalTurns,
        consecutiveWins: consecutiveWins,
        grids: grids,
        // 保留原始jackpot数据
        originalData: {
          consecutivePrize: bar.consecutivePrize,
          turnCount: bar.turnCount,
          timestamp: bar.timestamp
        }
      };
    });
  };

  // 生成弹珠机履历数据 - 每列代表一轮 (备用模拟数据)
  const generateMockHistoryData = () => {
    const rounds = [];
    // 确保生成至少20条记录用于图表显示
    const totalRounds = Math.max(20, isMobile ? 50 : 100);
    
    for (let roundIndex = 0; roundIndex < totalRounds; roundIndex++) {
      // 每轮的总转数 (200-1500转)
      const totalTurns = Math.floor(Math.random() * 1300) + 200;
      // 每100转一个格子
      const totalGrids = Math.ceil(totalTurns / 100);
      // 连奖次数 (0-5次)
      const consecutiveWins = Math.floor(Math.random() * 6);
      
      const grids = [];
      // 限制最多10格
      const maxGrids = Math.min(10, totalGrids);
      
      for (let gridIndex = 0; gridIndex < maxGrids; gridIndex++) {
        const turns = Math.min(100, Math.max(0, totalTurns - gridIndex * 100));
        const isActive = gridIndex < totalGrids && turns > 0;
        
        grids.push({
          id: `${roundIndex}-${gridIndex}`,
          turns: isActive ? turns : 0,
          isActive: isActive,
          roundIndex: roundIndex,
          gridIndex: gridIndex
        });
      }
      
      rounds.push({
        id: roundIndex,
        totalTurns: totalTurns,
        consecutiveWins: consecutiveWins,
        grids: grids
      });
    }
    
    return rounds;
  };

  // 渲染单个格子
  const renderGrid = (grid, totalTurns) => {
    let className = 'history-grid';
    
    if (grid.isActive) {
      // 计算是否应该显示为红色格子
      // 当总转数超过1000时，从底部开始计算红色格子数量
      const shouldBeRed = totalTurns > 1000;
      const excessTurns = totalTurns - 1000;
      const redGridCount = shouldBeRed ? Math.ceil(excessTurns / 100) : 0;
      
      // 格子是从底部开始排列的，gridIndex = 0 是最底部的格子
      const isRedGrid = shouldBeRed && grid.gridIndex < redGridCount;
      
      if (isRedGrid) {
        className += ' active red';
      } else {
        className += ' active';
      }
    } else {
      className += ' inactive';
    }
    
    return (
      <div key={grid.id} className={className} title={`${grid.turns}转`}>
      </div>
    );
  };

  // 渲染单列(一轮jackpot)
  const renderColumn = (round) => {
    return (
      <div key={round.id} className="history-column">
        <div className="round-header">
          <div className="round-number">{round.id + 1}</div>
          <div className="total-turns">{round.totalTurns}</div>
        </div>
        <div className="grids-column">
          {round.grids.map(grid => renderGrid(grid, round.totalTurns))}
        </div>
        <div className="consecutive-wins">{round.consecutiveWins}</div>
      </div>
    );
  };

  const stats = {
    totalRounds: historyData.length,
    totalTurns: historyData.reduce((sum, round) => sum + round.totalTurns, 0),
    totalWins: historyData.reduce((sum, round) => sum + round.consecutiveWins, 0),
    avgTurns: historyData.length > 0 ? Math.round(historyData.reduce((sum, round) => sum + round.totalTurns, 0) / historyData.length) : 0,
    avgWins: historyData.length > 0 ? (historyData.reduce((sum, round) => sum + round.consecutiveWins, 0) / historyData.length).toFixed(1) : '0.0'
  };

  // 在数据获取完成后添加调试日志
  useEffect(() => {
    if (historyData && historyData.length > 0) {
      console.log(`[HistoryModal] 履历数据加载完成:`, {
        dataCount: historyData.length,
        sampleData: historyData.slice(0, 3),
        viewMode,
        chartType
      });
    } else {
      console.log(`[HistoryModal] 暂无履历数据`, { deviceId, loading, error });
    }
  }, [historyData, viewMode, chartType, deviceId, loading, error]);

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size={isMobile ? "lg" : "xl"} 
      centered
      className="history-modal"
      scrollable
    >
      <Modal.Header closeButton className="history-modal-header">
        <Modal.Title>当り履歴 {error && <small className="text-warning">(使用模拟数据)</small>}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="history-modal-body">
        {loading ? (
          <div className="loading">読み込み中...</div>
        ) : error ? (
          <div className="error-message mb-3">
            <small className="text-muted">⚠️ {error} - 显示模拟数据</small>
          </div>
        ) : null}
        
        {/* 视图模式切换按钮 */}
        <div className="view-controls mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              {deviceInfo && (
                <span className="device-name">
                  {deviceInfo.name || `设备 ${deviceInfo.id}`}
                </span>
              )}
            </div>
            <div className="view-buttons">
              <ButtonGroup size={isMobile ? "sm" : "md"}>
                <Button
                  variant={viewMode === 'grid' ? 'primary' : 'outline-primary'}
                  onClick={() => setViewMode('grid')}
                >
                  格子视图
                </Button>
                <Button
                  variant={viewMode === 'chart' ? 'primary' : 'outline-primary'}
                  onClick={() => setViewMode('chart')}
                >
                  图表视图
                </Button>
              </ButtonGroup>
              
              {/* 图表类型切换按钮 - 仅在图表视图时显示 */}
              {viewMode === 'chart' && (
                <ButtonGroup size={isMobile ? "sm" : "md"} className="ms-2">
                  <Button
                    variant={chartType === 'line' ? 'success' : 'outline-success'}
                    onClick={() => setChartType('line')}
                  >
                    K线图
                  </Button>
                  <Button
                    variant={chartType === 'bar' ? 'success' : 'outline-success'}
                    onClick={() => setChartType('bar')}
                  >
                    柱状图
                  </Button>
                </ButtonGroup>
              )}
            </div>
          </div>
        </div>
        
        <div className="history-container">
          {/* 格子视图 */}
          {viewMode === 'grid' && (
            <>
              <div className="history-grid-container">
                {historyData.length > (isMobile ? 10 : 15) && (
                  <div className="scroll-hint">
                    <small>← 滑动查看更多履历 →</small>
                  </div>
                )}
                <div className="columns-container">
                  {historyData.map(round => renderColumn(round))}
                </div>
              </div>
              
              <div className="history-stats">
                <div className="stat-item">
                  <span>总次数</span>
                  <span>{stats.totalRounds}</span>
                </div>
                <div className="stat-item">
                  <span>总转数</span>
                  <span>{stats.totalTurns}</span>
                </div>
                <div className="stat-item">
                  <span>总连奖</span>
                  <span>{stats.totalWins}</span>
                </div>
                <div className="stat-item">
                  <span>平均转数</span>
                  <span>{stats.avgTurns}</span>
                </div>
                <div className="stat-item">
                  <span>平均连奖数</span>
                  <span>{stats.avgWins}</span>
                </div>
              </div>
            </>
          )}
          
          {/* 图表视图 */}
          {viewMode === 'chart' && (
            <div className="chart-container">
              <JackpotChart 
                data={historyData}
                chartType={chartType}
                isMobile={isMobile}
              />
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="history-modal-footer">
        <Button 
          variant="secondary" 
          onClick={onHide}
          className={`close-button ${isMobile ? 'mobile' : ''}`}
        >
          {isMobile ? '关闭' : '关闭'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
